<template>
  <div 
    class="carousel-container" 
    v-if="validAdvertisements.length > 0" 
    @touchstart="handleTouchStart" 
    @touchmove="handleTouchMove" 
    @touchend="handleTouchEnd"
    role="region"
    :aria-label="$t('advertisements.carouselTitle')"
    :aria-describedby="'carousel-description'"
  >
    <div id="carousel-description" class="sr-only">{{ $t('advertisements.carouselDescription') }}</div>
    <div class="carousel-track">
      <div
        v-for="(ad, idx) in validAdvertisements"
        :key="ad.id"
        class="card"
        :class="getCardClass(idx)"
        @click="handleClick(ad)"
      >
        <img :src="ad.image" :alt="ad.name" />
      </div>
    </div>
    <button 
      class="nav-arrow left" 
      @click="prev" 
      :disabled="isFirstSlide"
      :aria-label="$t('advertisements.previousSlide')"
    >
      {{ isRTL ? '›' : '‹' }}
    </button>
    <button 
      class="nav-arrow right" 
      @click="next" 
      :disabled="isLastSlide"
      :aria-label="$t('advertisements.nextSlide')"
    >
      {{ isRTL ? '‹' : '›' }}
    </button>
    <div class="dots" role="tablist" :aria-label="$t('advertisements.carouselDescription')">
      <span
        v-for="(dot, idx) in validAdvertisements.length"
        :key="idx"
        class="dot"
        :class="{ active: idx === currentSlide }"
        @click="goToSlide(idx)"
        role="tab"
        :aria-label="$t('advertisements.goToSlide') + ' ' + (idx + 1)"
        :aria-selected="idx === currentSlide"
      ></span>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import api from '@/services/api';

const advertisements = ref([]);
const currentSlide = ref(0);
const autoPlayInterval = 3500;
let autoPlayTimer = null;

// Touch/swipe variables
const touchStartX = ref(0);
const touchEndX = ref(0);
const minSwipeDistance = 50;

// RTL detection
const isRTL = computed(() => {
  return document.documentElement.dir === 'rtl' || 
         document.body.dir === 'rtl' || 
         getComputedStyle(document.documentElement).direction === 'rtl';
});

const validAdvertisements = computed(() => {
  return advertisements.value.filter(ad => ad && ad.id && ad.name);
});

const fetchAds = async () => {
  try {
    const res = await api.getAdvertisements();
    if (res.data && res.data.results) {
      advertisements.value = res.data.results;
    } else if (Array.isArray(res.data)) {
      advertisements.value = res.data;
    } else {
      advertisements.value = [];
    }
  } catch (e) {
    advertisements.value = [];
  }
};

onMounted(() => {
  fetchAds();
  startAutoPlay();
});

const isFirstSlide = computed(() => currentSlide.value === 0);
const isLastSlide = computed(() => currentSlide.value === validAdvertisements.value.length - 1);

function next() {
  if (currentSlide.value < validAdvertisements.value.length - 1) {
    currentSlide.value++;
  } else {
    currentSlide.value = 0; // Loop back to first
  }
}

function prev() {
  if (currentSlide.value > 0) {
    currentSlide.value--;
  } else {
    currentSlide.value = validAdvertisements.value.length - 1; // Loop to last
  }
}

function goToSlide(idx) {
  currentSlide.value = idx;
}

function handleClick(ad) {
  if (!ad) return;
  if (ad.is_url && ad.url) {
    window.open(ad.url, '_blank');
  } else if (ad.book) {
    window.location.href = `/books/${ad.book}/`;
  }
}

// Touch/swipe handlers
function handleTouchStart(event) {
  touchStartX.value = event.touches[0].clientX;
}

function handleTouchMove(event) {
  touchEndX.value = event.touches[0].clientX;
}

function handleTouchEnd() {
  const distance = touchStartX.value - touchEndX.value;
  const isLeftSwipe = distance > minSwipeDistance;
  const isRightSwipe = distance < -minSwipeDistance;

  if (isLeftSwipe) {
    next();
  } else if (isRightSwipe) {
    prev();
  }
}

function startAutoPlay() {
  stopAutoPlay();
  autoPlayTimer = setInterval(() => {
    next();
  }, autoPlayInterval);
}

function stopAutoPlay() {
  if (autoPlayTimer) clearInterval(autoPlayTimer);
}

// Card positioning logic
function getCardClass(idx) {
  const total = validAdvertisements.value.length;
  if (total === 0) return '';
  
  const center = currentSlide.value;
  const distance = idx - center;
  
  // Handle wrapping for circular carousel
  let adjustedDistance = distance;
  if (adjustedDistance > total / 2) {
    adjustedDistance -= total;
  } else if (adjustedDistance < -total / 2) {
    adjustedDistance += total;
  }
  
  if (adjustedDistance === 0) return 'center';
  if (adjustedDistance === 1) return 'right-1';
  if (adjustedDistance === -1) return 'left-1';
  if (adjustedDistance === 2) return 'right-2';
  if (adjustedDistance === -2) return 'left-2';
  if (adjustedDistance > 2 || adjustedDistance < -2) return 'hidden';
  
  return '';
}
</script>

<style scoped>
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.carousel-container {
  width: 100%;
  max-width: 1200px;
  height: 450px;
  position: relative;
  perspective: 1000px;
  margin: 0 auto;
}

.carousel-track {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  transform-style: preserve-3d;
  transition: transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.card {
  position: absolute;
  width: 280px;
  height: 410px;
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  cursor: pointer;
  padding: 0px;
}

.card img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.card.center {
  z-index: 10;
  transform: scale(1.1) translateZ(0);
}

.card.center img {
  filter: none;
}

.card.left-2 {
  z-index: 1;
  transform: translateX(-400px) scale(0.8) translateZ(-300px);
  opacity: 0.7;
}

.card.left-2 img {
  filter: grayscale(100%);
}

.card.left-1 {
  z-index: 5;
  transform: translateX(-200px) scale(0.9) translateZ(-100px);
  opacity: 0.9;
}

.card.left-1 img {
  filter: grayscale(100%);
}

.card.right-1 {
  z-index: 5;
  transform: translateX(200px) scale(0.9) translateZ(-100px);
  opacity: 0.9;
}

.card.right-1 img {
  filter: grayscale(100%);
}

.card.right-2 {
  z-index: 1;
  transform: translateX(400px) scale(0.8) translateZ(-300px);
  opacity: 0.7;
}

.card.right-2 img {
  filter: grayscale(100%);
}

.card.hidden {
  opacity: 0;
  pointer-events: none;
}

.nav-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 153, 255, 0.85);
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 20;
  transition: all 0.3s ease;
  font-size: 1.5rem;
  border: none;
  outline: none;
  padding-bottom: 4px;
}

.nav-arrow:hover:not(:disabled) {
  background: var(--primary-dark);
  transform: translateY(-50%) scale(1.1);
}

.nav-arrow:disabled {
  background: rgba(0, 0, 0, 0.3);
  cursor: not-allowed;
  transform: translateY(-50%) scale(0.9);
}

.nav-arrow.left {
  left: 20px;
  padding-right: 3px;
}

.nav-arrow.right {
  right: 20px;
  padding-left: 3px;
}

.dots {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 60px;
}

.dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: var(--secondary-color);
  cursor: pointer;
  transition: all 0.3s ease;
}

.dot.active {
  background: var(--primary-color);
  transform: scale(1.2);
}

@media (max-width: 768px) {
  .carousel-container {
    height: 350px;
  }

  .card {
    width: 200px;
    height: 280px;
  }

  .card.left-2 {
    transform: translateX(-250px) scale(0.8) translateZ(-300px);
  }

  .card.left-1 {
    transform: translateX(-120px) scale(0.9) translateZ(-100px);
  }

  .card.right-1 {
    transform: translateX(120px) scale(0.9) translateZ(-100px);
  }

  .card.right-2 {
    transform: translateX(250px) scale(0.8) translateZ(-300px);
  }
}
</style> 